import { useResolve, useResolveController } from '@visiba/instantiation';
import { AuthController } from './auth.controller';
import { Async } from '@component/async.component';
import { Divider, Flex, NotificationAlert, SafeArea, Text, TextLink } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Shimmer } from '@component/new_shimmer.component';
import { withTranslation } from '@application/i18n/with_translation';
import { AuthMethodLink } from './_auth_method_link.component';
import { UniversalConfig } from '@application/universal_config.service';
import styled from 'styled-components';

export function AuthMethods({ returnUrl }: Props): JSX.Element {
	const controller = useResolveController(AuthController);
	const universalConfig = useResolve(UniversalConfig);

	return (
		<SafeArea maxWidth={'600px'}>
			<Flex gap={tokens.spacing.x2} color={tokens.color.content.default} style={{ width: '100%' }}>
				<Text.h1 variant='headingLarge'>{withTranslation('Feature.Auth.Methods.Header')}</Text.h1>
				<Text.p variant='bodyLargeRegular'>{withTranslation('Feature.Auth.Methods.Description')}</Text.p>

				<Async
					derive={controller.methods}
					fallback={
						<>
							<Flex gap={tokens.spacing.x1}>
								<Shimmer height={16} opacity={1} cornerRadius={6} width={'30%'} />
								<Shimmer height={64} opacity={1} />
								<Shimmer height={64} opacity={0.6} />
								<Shimmer height={64} opacity={0.4} />
							</Flex>
							<Divider decorative />
							<Shimmer height={24} opacity={0.4} cornerRadius={6} width={'50%'} />
						</>
					}
					catch={<NotificationAlert variant='danger'>{withTranslation('Feature.Auth.Methods.FetchError')}</NotificationAlert>}
				>
					{(methods) => (
						<>
							<Flex gap={tokens.spacing.x1} style={{ margin: `${tokens.spacing.x0_5} 0` }}>
								<Text.h2 variant='labelSemiBold'>{withTranslation('Feature.Auth.Methods.Label')}</Text.h2>
								{methods.map((method) => (
									<AuthMethodLink
										key={method.scheme}
										scheme={method.scheme}
										title={method.name}
										description={method.description ?? undefined}
										onClick={() => controller.selectMethod(method, returnUrl)}
									/>
								))}
							</Flex>

							<Divider decorative />

							<StyledTextWrapper>
								<Text.p style={{ display: 'inline' }} variant='bodySmallRegular'>
									{withTranslation('Common.PP.ReadMore')}
								</Text.p>{' '}
								<TextLink size='small' href={universalConfig.legal.pp} target='_blank' icon='linkOut' iconPosition='trailing'>
									{withTranslation('Common.PP')}
								</TextLink>
							</StyledTextWrapper>
						</>
					)}
				</Async>
			</Flex>
		</SafeArea>
	);
}

const StyledTextWrapper = styled.div`
	display: inline;
`;

interface Props {
	returnUrl: string;
}
